import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { usePanel } from 'hooks/usePanel';
import { GenericLoading } from 'components/ui/Loader';
import { MandantsTunnelsSelector } from 'back-office/shared/components/mandants/MandantsTunnelsSelector';
import { MandantSelection, MandantSelectorPlateforme } from 'back-office/shared/components/mandants/mandantTypes';
import { mandantsArraySchema } from 'back-office/shared/components/mandants/mandantsValidation';
import { MiaListItemDto, updateMiaMandants } from '../api/miasApi';

interface MiaMandantsPanelProps {
  mia: MiaListItemDto;
  onSuccess?: () => void;
}

interface MandantsFormValues {
  mandants: MandantSelection[];
}

const validationSchema = Yup.object({
  mandants: mandantsArraySchema,
});

/**
 * État cible des mandants d'un MIA, tunnels par mandant inclus (granularité
 * paire mandant×tunnel). Le backend resynchronise les habilitations dérivées :
 * pose des nouvelles paires, désactivation en cascade des orphelines — le
 * bilan est remonté en notification.
 */
export const MiaMandantsPanel: React.FC<MiaMandantsPanelProps> = ({ mia, onSuccess }) => {
  const formikRef = useRef<FormikProps<MandantsFormValues>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setPanelActions, closePanel } = usePanel();
  const referentialsData = useAppSelector(selectReferentialData);

  // Plateformes du MIA (id → libellé) pour grouper les mandants proposables.
  const plateformes = useMemo<MandantSelectorPlateforme[]>(() => {
    const orgRefs = referentialsData[ReferentialKey.Plateforme] ?? [];
    return (mia.plateformeIds ?? []).flatMap((id) => {
      const ref = orgRefs.find((r) => r.id === id);
      return ref ? [{ id, label: ref.label }] : [{ id, label: id }];
    });
  }, [mia.plateformeIds, referentialsData]);

  // Libellés des mandants déjà en place (affichage des cartes en édition).
  const initialLabels = useMemo(
    () =>
      Object.fromEntries(
        (mia.mandants ?? []).flatMap((m) => (m.label ? [[m.courtierId, m.label]] : [])),
      ),
    [mia.mandants],
  );

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
    });
  }, [setPanelActions]);

  const handleSubmit = async (values: MandantsFormValues) => {
    setIsLoading(true);
    try {
      const response = await updateMiaMandants(mia.courtierId, {
        mandants: values.mandants,
      });

      if (response?.isSuccess) {
        if (response.notification?.message) {
          // Bilan des habilitations posées/désactivées → informer l'utilisateur
          toast.warning(response.notification.message);
        } else {
          toast.success('Mandants mis à jour');
        }
        onSuccess?.();
        closePanel();
      } else if (response?.errorMessage) {
        toast.error(response.errorMessage);
      }
    } catch (error) {
      console.error('Error updating MIA mandants:', error);
      toast.error('Erreur lors de la mise à jour des mandants');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}

      <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
        L&apos;accès aux tunnels se donne PAR mandant : décocher un tunnel ou
        retirer un mandant désactive automatiquement les habilitations du MIA
        qui ne sont plus couvertes.
      </div>

      <Formik<MandantsFormValues>
        initialValues={{
          mandants: (mia.mandants ?? []).map((m) => ({
            courtierId: m.courtierId,
            tunnelIds: m.tunnelIds ?? [],
          })),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        <Form>
          {/* Courtiers des plateformes du MIA, groupés par plateforme */}
          <MandantsTunnelsSelector
            name="mandants"
            plateformes={plateformes}
            initialLabels={initialLabels}
          />
        </Form>
      </Formik>
    </div>
  );
};
